import { Box, Flex, Modal, ModalBody, Spinner, Text, useDisclosure } from '@televet/kibble-ui';
import { useTracking } from 'shared/providers/TrackingProvider';
import { DashboardFrame, Parameter } from 'amazon-quicksight-embedding-sdk/dist/types';
import sortBy from 'lodash-es/sortBy';
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { RouteBasePaths } from 'routes';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { useSidePanel } from 'shared/components/SidePanel/state/providers/SidePanelProvider';
import { GraphQLFetchPolicies } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  GetQuickSightDashboardsQuery,
  useGetQuickSightEmbedUrlLazyQuery,
  useGetSignedDownloadUrlMutation,
} from 'shared/types/graphql';
import styled from 'styled-components/macro';
import { quickSightRelativePath } from '..';
import CallRecordingTranscriptSummary from './CallRecordingTranscriptSummary';
import embedDashboard, { QUICKSIGHT_DASHBOARD_URL } from './embedDashboard';

type QuickSightReportListProps = {
  quickSightDashboardData: GetQuickSightDashboardsQuery | undefined;
  isQuickSightDashboardDataLoading: boolean;
  setDashboardName: React.Dispatch<React.SetStateAction<string>>;
};

const QuickSightReportList = ({
  quickSightDashboardData,
  isQuickSightDashboardDataLoading,
  setDashboardName,
}: QuickSightReportListProps): JSX.Element => {
  const { url } = useRouteMatch();
  const [experience, setExperience] = useState<DashboardFrame>();
  const { trackEvent } = useTracking();

  const [getDashboardURL, { data }] = useGetQuickSightEmbedUrlLazyQuery({
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });
  const [currentDashboardId, setCurrentDashboardId] = useState<string>('');

  const isIframeInDom = (): boolean => {
    return document.getElementById('experience-container') !== null;
  };

  const location = useLocation();
  const { path } = useRouteMatch();
  const history = useHistory();

  const {
    isOpen: isTranscriptSummaryModalOpen,
    onOpen: onTranscriptSummaryModalOpen,
    onClose: onTranscriptSummaryModalClose,
  } = useDisclosure();

  const [callHistoryId, setCallHistoryId] = useState<string>('');

  useEffect(() => {
    // Use params does not work here because we conditionally render the
    // route component
    const pathRe = new RegExp(`.*\\${quickSightRelativePath}\\/(.*)`);

    const [, dashBoardURLName] = pathRe.exec(location.pathname) || [];
    const dashboardId = quickSightDashboardData?.getQuickSightDashboards?.find(
      (dashboard) => dashboard?.Name?.toLowerCase().replaceAll(' ', '-') === dashBoardURLName,
    )?.DashboardId;
    if (dashBoardURLName && dashboardId && !currentDashboardId) {
      setCurrentDashboardId(dashboardId);
    }
    if (quickSightDashboardData?.getQuickSightDashboards && currentDashboardId) {
      const currentDashboard = quickSightDashboardData?.getQuickSightDashboards.find(
        (dashboard) => dashboard?.DashboardId === currentDashboardId,
      );
      setDashboardName((prevState) => (prevState ? prevState : currentDashboard?.Name || ''));
    }

    // This is necessary due to the URL being hardcoded in ChurnZero and we
    // need to redirect to the correct dashboard
    if (
      location.pathname.indexOf('televet_embed-clinic_performance_report') >= 0 ||
      location.pathname.indexOf('otto_embed-clinic_performance_report') >= 0
    ) {
      history.push(
        `${RouteBasePaths.Reporting}${quickSightRelativePath}/${QUICKSIGHT_DASHBOARD_URL.Clinic_Performance}`,
      );
    }
  }, [currentDashboardId, history, location, path, quickSightDashboardData?.getQuickSightDashboards, setDashboardName]);

  useEffect(() => {
    if (currentDashboardId) {
      getDashboardURL({
        variables: { where: { dashboardId: currentDashboardId } },
      });
    }
  }, [currentDashboardId, getDashboardURL]);

  const { currentClinic } = useClinicUser();

  const dashboardParameters: Parameter[] = useMemo(
    () => [{ Name: 'ClinicName', Values: [currentClinic?.name || ''] }],
    [currentClinic?.name],
  );

  //To handle dashboard clicks

  const [isDownloadingCall, setIsDownloadingCall] = useState(false);

  const [downloadUrlAndFileName, setDownloadUrlAndFileName] = useState({ downloadUrl: '', fileName: '' });
  const [selectedPetParentId, setSelectedPetParentId] = useState<string>('');
  const [getSignedDownloadURL] = useGetSignedDownloadUrlMutation({
    onCompleted: (data) => {
      const signedURL = data.getSignedDownloadUrl;
      if (signedURL) {
        try {
          setIsDownloadingCall(true);
          fetch(signedURL, { headers: { 'Access-Control-Allow-Origin': '*' } })
            .then((res) => {
              return res.blob();
            })
            .then((blob) => {
              const fileExtension = signedURL.split('?')?.[0]?.split('.')?.pop();
              const objectURL = URL.createObjectURL(blob);
              const ae = document.createElement('a');
              ae.href = objectURL;
              ae.download = `${downloadUrlAndFileName.fileName}${fileExtension ? `.${fileExtension}` : ''}`;
              ae.click();
              setIsDownloadingCall(false);
              setDownloadUrlAndFileName({ downloadUrl: '', fileName: '' });
            });
        } catch (error) {
          console.log('error downloading call', error);
          setIsDownloadingCall(false);
        }
      }
    },
  });

  useEffect(() => {
    if (downloadUrlAndFileName.downloadUrl) {
      getSignedDownloadURL({
        variables: { assetUrl: downloadUrlAndFileName.downloadUrl },
      });
    }
  }, [downloadUrlAndFileName.downloadUrl, getSignedDownloadURL]);

  const { viewClinicPetParent } = useSidePanelSearch();
  const { setIsOpen: setIsSidePanelOpen } = useSidePanel();
  useEffect(() => {
    if (selectedPetParentId) {
      setIsSidePanelOpen(true);
      viewClinicPetParent({ clinicPetParentId: selectedPetParentId });
      setSelectedPetParentId('');
    }
  }, [selectedPetParentId, setIsSidePanelOpen, viewClinicPetParent]);

  // Create a new experience if the embed URL changes
  // we don't generate a new embed url if the dashboardId changes
  // only if the iframe is not in the dom, to navigate to a different dashboard
  // we use the method navigateToDashboard from the experience
  useEffect(() => {
    const runEmbedDashboard = async (): Promise<void> => {
      if (data?.getQuickSightEmbedURL?.EmbedUrl) {
        const newExperience = await embedDashboard(
          data?.getQuickSightEmbedURL?.EmbedUrl,
          dashboardParameters,
          setDownloadUrlAndFileName,
          setSelectedPetParentId,
          onTranscriptSummaryModalOpen,
          setCallHistoryId,
          trackEvent,
        );
        setExperience(newExperience);
      }
    };
    runEmbedDashboard();
  }, [dashboardParameters, data?.getQuickSightEmbedURL?.EmbedUrl, onTranscriptSummaryModalOpen, trackEvent]);

  return isQuickSightDashboardDataLoading ? (
    <Flex justifyContent="center" alignItems="center" minHeight="100px">
      <Spinner />
    </Flex>
  ) : (
    <>
      <Modal
        size="sm"
        onClose={(): void => {
          // This component requires a onClose prop but we are not using it at this time
        }}
        isOpen={isDownloadingCall}
      >
        <ModalBody textAlign="center" mt="2" mb="2">
          <Box>Preparing file to download</Box>
          <Box marginTop="3">
            <Spinner />
          </Box>
          <Text marginTop="2" as="p">
            {downloadUrlAndFileName.fileName}
          </Text>
        </ModalBody>
      </Modal>
      <CallRecordingTranscriptSummary
        callHistoryId={callHistoryId}
        isTranscriptionSummaryModalOpen={isTranscriptSummaryModalOpen}
        onTranscriptionSummaryModalClose={onTranscriptSummaryModalClose}
      />
      {sortBy(quickSightDashboardData?.getQuickSightDashboards, (dashboard) => dashboard?.Name)?.map((dashboard) => {
        return (
          <StyledLink
            id={dashboard?.DashboardId || ''}
            to={`${url}/quicksight/${dashboard?.Name?.toLowerCase().replaceAll(' ', '-')}`}
            key={dashboard?.DashboardId}
            exact
            onClick={async (): Promise<void> => {
              setDashboardName(dashboard?.Name || '');
              if (!isIframeInDom()) {
                await getDashboardURL({
                  variables: { where: { dashboardId: dashboard?.DashboardId || '' } },
                });
              } else if (!data?.getQuickSightEmbedURL?.EmbedUrl) {
                setCurrentDashboardId(dashboard?.DashboardId || '');
              } else {
                try {
                  experience?.navigateToDashboard(dashboard?.DashboardId || '', {
                    parameters: dashboardParameters,
                  });
                } catch (e) {
                  console.log('error', e);
                }
              }
            }}
          >
            {dashboard?.Name}
          </StyledLink>
        );
      })}
    </>
  );
};

const StyledLink = styled(NavLink)`
  text-decoration: none;
  display: block;
  padding: 12px 20px;
  transition: color 0.1s ease-in-out;
  color: var(--chakra-colors-text-default);

  &:hover {
    background: var(--chakra-colors-background-primaryAlpha-hover);
  }

  &.active {
    color: var(--chakra-colors-text-brand);
    background: var(--chakra-colors-background-primaryAlpha-pressed);
  }
`;

export default QuickSightReportList;
